<script setup lang="ts">
import {
    EntityNameSpan,
    type NamedNode,
} from '@precon-tools-suite-by-shared/shared-names-ui';
import Tag from 'primevue/tag';
import Skeleton from 'primevue/skeleton';
import TagIconComponent from './TagIcon.vue';
import type { TagIconTheme } from '../lib/icons';
import {
    useColorClass,
    type ColorTint,
} from '@precon-tools-suite-by-shared/shared-colors-ui';
import {
    Color,
    TagIcon,
} from '@precon-tools-suite-by-shared/precon-graphql-types';
import type { StyleValue } from 'vue';

export type TagNode = Partial<NamedNode> & {
    id: string;
    icon?: TagIcon | null | undefined;
    color?: Color | null | undefined;
};

const props = withDefaults(
    defineProps<{
        node?: TagNode | null | undefined;
        name?: 'full' | 'short' | 'none';
        icons?: TagIconTheme | 'none';
        size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl';
        bgTint?: ColorTint;
        borderTint?: ColorTint;
        iconTint?: ColorTint;
        textTint?: ColorTint;
        closeIcon?: string;
        closable?: boolean;
        loading?: boolean;
        loadingStyle?: StyleValue;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        loadingClass?: any;
    }>(),
    {
        node: undefined,
        icons: 'pi-normal',
        size: 'sm',
        bgTint: '200',
        iconTint: '700',
        textTint: '800',
        borderTint: ({ bgTint }) => bgTint ?? '200',
        closeIcon: 'pi pi-times',
        name: 'short',
        loadingStyle: () => 'width: 5rem; height: 1.5rem',
        loadingClass: () => [],
    }
);

const emit = defineEmits<{
    (e: 'close:click', event: MouseEvent): void;
}>();

const color = useColorClass(() => props.node?.color ?? Color.Gray);

function onClose(event: MouseEvent) {
    if (props.closable) {
        emit('close:click', event);
    }
}
</script>

<template>
    <template v-if="loading">
        <slot name="skeleton">
            <Skeleton
                :style="loadingStyle"
                :class="loadingClass"
                style="display: inline-block"
            />
        </slot>
    </template>
    <template v-else>
        <Tag
            data-cy="tag"
            :data-node="node?.id"
            :class="[
                'tag-display',
                color.bg(bgTint),
                'border-1',
                color.border(borderTint),
            ]"
        >
            <template #icon>
                <i
                    v-if="closable"
                    data-cy="close"
                    :class="[
                        'tag-close-icon',
                        closeIcon,
                        'mr-1',
                        color.text('800'),
                    ]"
                    @click="onClose"
                ></i>
                <TagIconComponent
                    v-if="icons !== 'none'"
                    :icon="node?.icon"
                    :theme="icons"
                    :color="node?.color"
                    :tint="iconTint"
                    :size="size"
                />
            </template>
            <slot :node="node">
                <EntityNameSpan
                    v-if="name !== 'none'"
                    :node="node"
                    :size="name"
                    :class="['tag-name', `text-${size}`, color.text(textTint)]"
                    placeholder="- ??? -"
                    :placeholder-class="['font-italic', color.text('500')]"
                />
            </slot>
        </Tag>
    </template>
</template>

<style class="scss">
.tag-display {
    .tag-close-icon {
        font-size: 0.75rem;
        opacity: 0.25;
        cursor: pointer;
        &:hover {
            opacity: 1;
        }
    }
    .tag-name {
        margin-left: 0.5rem;
        margin-right: 0.5rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}
</style>
